import { useEffect, useRef, useState } from "react";

// Must match the activityOut animation duration in tailwind.config.js.
const EXIT_MS = 260;
// How long an entering row keeps its animation flag before settling to a plain, resting row —
// long enough for the enter animation and its highlight (see tailwind.config.js) to finish.
const ENTER_SETTLE_MS = 1400;

/**
 * Wraps a list of `{ id, ... }` items so entries that disappear stay mounted briefly
 * (flagged `leaving: true`) long enough to play an exit animation before unmounting.
 * New items pass straight through and animate in on mount (the list is keyed by id) — except
 * the initial load and items appended below everything already shown (`entering: false`): that's
 * the backfill or an older page scrolled in, not fresh activity, and animating rows from zero
 * height would briefly leave the list's tail in view and trip the next page load.
 */
export function useAnimatedList(items) {
  const [rendered, setRendered] = useState(() =>
    items.map((i) => ({ ...i, leaving: false, entering: false })),
  );
  const timers = useRef(new Map());

  // Reconcile incoming items against what's on screen: keep present items, and retain any
  // that vanished — flagged leaving — so they can animate out.
  useEffect(() => {
    setRendered((prev) => {
      const present = new Set(items.map((i) => i.id));
      const known = new Map(prev.map((i) => [i.id, i]));
      // Everything after the last already-shown item is appended (paged in); anything new before
      // it arrived at the top or in between and is genuinely new.
      let lastKnownIndex = -1;
      items.forEach((i, index) => {
        if (known.has(i.id)) lastKnownIndex = index;
      });
      const next = items.map((i, index) => {
        const seen = known.get(i.id);
        const entering = seen
          ? seen.entering
          : known.size > 0 && index < lastKnownIndex;
        return { ...i, leaving: false, entering };
      });
      const stillLeaving = prev
        .filter((i) => !present.has(i.id))
        .map((i) => ({ ...i, leaving: true }));
      return [...next, ...stillLeaving];
    });
  }, [items]);

  // Once an item is flagged leaving, schedule its removal after the exit animation.
  useEffect(() => {
    for (const item of rendered) {
      if (item.leaving && !timers.current.has(item.id)) {
        const timer = setTimeout(() => {
          timers.current.delete(item.id);
          setRendered((cur) => cur.filter((x) => x.id !== item.id));
        }, EXIT_MS);
        timers.current.set(item.id, timer);
      }
    }
  }, [rendered]);

  // Once an entering row has played its animation, clear the flag so it settles to a plain resting
  // row (the animation wrapper is only stable while actually animating — see ActivityPanel).
  useEffect(() => {
    for (const item of rendered) {
      const key = `enter:${item.id}`;
      if (item.entering === true && !item.leaving && !timers.current.has(key)) {
        const timer = setTimeout(() => {
          timers.current.delete(key);
          setRendered((cur) =>
            cur.map((x) => (x.id === item.id ? { ...x, entering: false } : x)),
          );
        }, ENTER_SETTLE_MS);
        timers.current.set(key, timer);
      }
    }
  }, [rendered]);

  useEffect(() => {
    const timersMap = timers.current;
    return () => timersMap.forEach((t) => clearTimeout(t));
  }, []);

  return rendered;
}
